import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import AddressSopDialog from './AddressSopDialog';
import { getAddressSops, deletePersonSop } from '../../services/userService';
import { SOP_GRID_COLUMNS } from '../../constants/addressSopsTabConstants';

const AddressSopsTab = ({ ptsAddressID, onSopsChanged }) => {
  const dispatch = useDispatch();
  const canEditAddress = getFullPermissions('globals', 'Addresses', 'any').Edit;
  const [loading, setLoading] = useState(false);
  const [sopLinks, setSopLinks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSopLink, setEditingSopLink] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadSopLinks = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAddressSops(ptsAddressID),
      onSuccess: (data) => setSopLinks(data),
    });
  }, [dispatch, ptsAddressID]);

  const existingSopIds = useMemo(() => sopLinks.map((link) => link.ptsSOPID), [sopLinks]);

  const handleAdd = useCallback(() => {
    setEditingSopLink(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingSopLink(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingSopLink(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadSopLinks();
    onSopsChanged?.();
  }, [handleFormClose, loadSopLinks, onSopsChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${row.SOP?.SOPID || 'SOP'}" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () =>
              deletePersonSop({
                ptsParentID: ptsAddressID,
                ParentType: 'Address',
                ptsSOPID: row.ptsSOPID,
              }),
            onSuccess: () => {
              dispatch(notify('SOP deleted successfully', 'success'));
              loadSopLinks();
              onSopsChanged?.();
            },
          });
        },
        'Delete SOP'
      );
    },
    [dispatch, ptsAddressID, requestConfirm, loadSopLinks, onSopsChanged]
  );

  useEffect(() => {
    loadSopLinks();
  }, [loadSopLinks]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add SOP"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditAddress}
        />
      </Box>

      <CommonDataGrid
        rows={sopLinks}
        columns={SOP_GRID_COLUMNS}
        getRowId={(row) => row.ptsSOPID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Address SOPs',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditAddress },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditAddress },
        }}
      />

      {formOpen && (
        <AddressSopDialog
          open={formOpen}
          ptsAddressID={ptsAddressID}
          sopLink={editingSopLink}
          existingSopIds={existingSopIds}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default AddressSopsTab;
